import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Select, Button, Tag, Tooltip, Switch,
  Empty, Modal, Radio, Divider, Alert, message, Input, Pagination
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined,
  InfoCircleOutlined, CheckCircleOutlined,
  CodeOutlined, BulbOutlined, CopyOutlined,
  ThunderboltOutlined, SettingOutlined, LockOutlined,
  UserOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { EngineeringAssertionForm, LLMAssertionForm } from './AssertionForms';
import { shadowLinkApi, judgeScenarioApi } from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

// Mock 当前用户信息
const CURRENT_USER = { name: '管理员', workid: 'admin', headImg: '', isAdmin: true };
const IS_ADMIN = CURRENT_USER.isAdmin === true || CURRENT_USER.workid === 'admin';

// 场景字段预设
const SCENARIO_FIELD_PRESETS = {
  '采购RT审核': {
    aiFields: ['decision', 'risk_level', 'reject_reasons', 'score'],
    humanFields: ['human_decision', 'human_risk_level', 'human_comments', 'final_score'],
  },
  '采购合规审查': {
    aiFields: ['compliance_result', 'violation_items', 'suggestion'],
    humanFields: ['manual_result', 'confirmed_violations', 'reviewer_note'],
  },
  '合同机审': {
    aiFields: ['review_result', 'risk_clauses', 'risk_score'],
    humanFields: ['legal_result', 'flagged_clauses', 'legal_score'],
  },
  '合同风险识别': {
    aiFields: ['risk_type', 'risk_description', 'severity'],
    humanFields: ['confirmed_risk_type', 'human_description', 'confirmed_severity'],
  },
  '证照合规审查': {
    aiFields: ['license_valid', 'expire_date', 'scope_match'],
    humanFields: ['manual_valid', 'confirmed_expire', 'manual_scope_match'],
  },
};

const DEFAULT_FIELDS = {
  aiFields: ['decision', 'result', 'score', 'reason'],
  humanFields: ['human_decision', 'human_result', 'human_score', 'human_reason'],
};

// Mock 初始配置数据
const INITIAL_CONFIGS = [
  {
    id: 1,
    name: '采购RT审核 - 决策字段精确匹配',
    domain: '采购',
    scenario: '采购RT审核',
    assertionType: 'engineering',
    enabled: true,
    engineering: { 
      code: `// 工程断言函数
function assert(aiOutput, humanOutput) {
  const aiDecision = aiOutput.decision;
  const humanDecision = humanOutput.human_decision;

  if (aiDecision === humanDecision) {
    return { pass: true, reason: \`决策一致：\${aiDecision}\` };
  }

  return {
    pass: false,
    reason: \`AI 决策 "\${aiDecision}" 与人工决策 "\${humanDecision}" 不一致\`
  };
}`
    },
    llm: {},
    createdAt: '2024-01-10',
    createdBy: 'admin',
    createdByName: '管理员',
  },
  {
    id: 2,
    name: '合同机审 - 风险条款语义一致性',
    domain: '合同',
    scenario: '合同机审',
    assertionType: 'llm',
    enabled: true,
    engineering: {},
    llm: {
      aiField: 'risk_clauses',
      humanField: 'flagged_clauses',
      semanticDescription: '判断 AI 识别的风险条款与法务标注的风险条款在语义上是否指向相同的合同风险点，允许表述不同但核心风险类型需一致',
      model: 'qwen-max',
    },
    createdAt: '2024-01-12',
    createdBy: 'admin',
    createdByName: '管理员',
  },
];

// 创建者标签组件
function OwnerTag({ createdBy, createdByName, isOwner }) {
  return (
    <Tooltip title={isOwner ? '你创建的规则' : `由 ${createdByName}（${createdBy}）创建，你只能复制`}>
      <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
        isOwner
          ? 'bg-blue-50 border-blue-200 text-blue-600'
          : 'bg-gray-50 border-gray-200 text-gray-400'
      }`}>
        {isOwner
          ? <UserOutlined className="text-blue-400" />
          : <LockOutlined className="text-gray-300" />
        }
        <span>{isOwner ? '我的' : createdByName}</span>
      </div>
    </Tooltip>
  );
}

// 配置卡片组件
function ConfigCard({ config, onEdit, onToggle, onDuplicate, onDelete, currentUserWorkid, isAdmin }) {
  const isOwner = config.createdBy === currentUserWorkid;
  // hasOperate 为 true 或者是管理员或创建者时拥有操作权限
  const canOperate = config.hasOperate || isOwner || isAdmin;
  const isEngineering = config.assertionType === 'engineering';
  const eng = config.engineering || {};
  const llm = config.llm || {};

  return (
    <Card
      className={`mb-3 transition-all duration-200 hover:shadow-md ${!config.enabled ? 'opacity-60' : ''}`}
      bodyStyle={{ padding: '16px 20px' }}
      style={{ border: config.enabled ? '1px solid #e8e8e8' : '1px solid #f0f0f0' }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">{config.name}</span>
            <Tag color={isEngineering ? 'blue' : 'purple'} className="text-xs">
              {isEngineering ? <><CodeOutlined className="mr-1" />工程断言</> : <><BulbOutlined className="mr-1" />LLM断言</>}
            </Tag>
            <Tag color="geekblue" className="text-xs">{config.domain}</Tag>
            <Tag color="cyan" className="text-xs">{config.scenario}</Tag>
            <OwnerTag
              createdBy={config.createdBy}
              createdByName={config.createdByName}
              isOwner={isOwner}
            />
          </div>

          {isEngineering && eng.code && (
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span className="font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">JS Code</span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-400 truncate max-w-md">
                {eng.code.substring(0, 60).replace(/\\n/g, ' ')}...
              </span>
            </div>
          )}

          {!isEngineering && (
            <div className="flex items-start gap-2 text-xs text-gray-500 flex-wrap">
              <span className="font-mono bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{llm.aiField}</span>
              <span className="text-gray-400">vs</span>
              <span className="font-mono bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{llm.humanField}</span>
              {llm.semanticDescription && (
                <>
                  <span className="text-gray-300">·</span>
                  <Tooltip title={llm.semanticDescription}>
                    <span className="text-gray-400 cursor-help truncate max-w-xs">{llm.semanticDescription.slice(0, 40)}...</span>
                  </Tooltip>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          <Tooltip title={!canOperate ? '只有创建者才能启用/禁用' : ''}>
            <Switch
              size="small"
              checked={config.enabled}
              onChange={(checked) => canOperate && onToggle(config.id, checked)}
              disabled={!canOperate}
            />
          </Tooltip>

          <Tooltip title="复制规则">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => onDuplicate(config)}
              className="text-gray-400 hover:text-blue-500"
            />
          </Tooltip>

          <Tooltip title={!canOperate ? '只有创建者或管理员才能编辑' : '编辑规则'}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => canOperate && onEdit(config)}
              disabled={!canOperate}
              className={canOperate ? 'text-gray-400 hover:text-blue-500' : 'text-gray-200'}
            />
          </Tooltip>

          <Tooltip title={!canOperate ? '只有创建者或管理员才能删除' : '删除规则'}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => canOperate && onDelete(config.id)}
              disabled={!canOperate}
            />
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}

// 将后端数据转换为前端格式
const transformBackendToFrontend = (item) => ({
  id: item.id,
  name: item.ruleName,
  domain: item.domainName,
  domainId: item.domainId,
  scenario: item.scenarioName,
  scenarioId: item.scenarioId,
  assertionType: item.assertionType?.toLowerCase(),
  enabled: item.enabled === 'Y',
  hasOperate: item.hasOperate,
  engineering: item.assertionType === 'ENGINEERING' ? { code: item.assertionCode } : {},
  llm: item.assertionType === 'LLM' ? {
    aiField: item.aiOutputPath,
    humanField: item.humanOutputPath,
    semanticDescription: item.semanticDescription,
    model: item.judgeModel?.toLowerCase() || 'qwen-max',
  } : {},
  createdAt: item.gmtCreate,
  createdBy: item.creator,
  createdByName: item.creatorName || item.creator,
  description: item.description,
});

// 将前端数据转换为后端格式
const transformFrontendToBackend = (config) => ({
  ruleName: config.name,
  domainId: config.domainId,
  scenarioId: config.scenarioId,
  assertionType: config.assertionType?.toUpperCase(),
  assertionCode: config.assertionType === 'engineering' ? config.engineering?.code : undefined,
  aiOutputPath: config.assertionType === 'llm' ? config.llm?.aiField : undefined,
  humanOutputPath: config.assertionType === 'llm' ? config.llm?.humanField : undefined,
  semanticDescription: config.assertionType === 'llm' ? config.llm?.semanticDescription : undefined,
  judgeModel: config.assertionType === 'llm' ? (config.llm?.model || 'qwen-max') : undefined,
  description: config.description,
});

function AssertionConfig() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterRuleName, setFilterRuleName] = useState('');
  const [filterDomainId, setFilterDomainId] = useState(undefined);
  const [filterScenarioId, setFilterScenarioId] = useState(undefined);
  const [filterType, setFilterType] = useState(undefined);
  const [filterOwner, setFilterOwner] = useState(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    pages: 1,
  });

  const currentUserWorkid = CURRENT_USER.workid;
  const currentUserName = CURRENT_USER.name;

  // 业务域选项
  const [domainOptions, setDomainOptions] = useState([]);
  // 业务场景选项
  const [scenarioOptions, setScenarioOptions] = useState([]);
  // 模型选项
  const [modelOptions, setModelOptions] = useState([]);

  // 获取业务域列表
  const fetchDomainOptions = async () => {
    try {
      const res = await shadowLinkApi.getBusinessDomains();
      setDomainOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务域列表失败:', error);
      // 接口异常时使用 mock 数据
      setDomainOptions([
        { value: '采购', label: '采购' },
        { value: '合同', label: '合同' },
        { value: '财务', label: '财务' },
      ]);
    }
  };

  // 获取业务场景列表
  const fetchScenarioOptions = async (domain) => {
    try {
      const res = await shadowLinkApi.getBusinessScenarios(domain);
      setScenarioOptions(res?.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
      // 接口异常时使用 mock 数据
      if (domain === '采购') {
        setScenarioOptions([
          { value: '采购RT审核', label: '采购RT审核' },
          { value: '采购合规审查', label: '采购合规审查' },
        ]);
      } else if (domain === '合同') {
        setScenarioOptions([
          { value: '合同机审', label: '合同机审' },
          { value: '合同风险识别', label: '合同风险识别' },
        ]);
      } else {
        setScenarioOptions([]);
      }
    }
  };

  // 获取模型类型列表
  const fetchModelOptions = async () => {
    try {
      const res = await judgeScenarioApi.getModelTypes();
      setModelOptions(res || []);
    } catch (error) {
      console.error('获取模型类型列表失败:', error);
      // 接口异常时使用 mock 数据
      setModelOptions([
        { value: 'qwen-max', label: 'Qwen-Max' },
        { value: 'qwen-plus', label: 'Qwen-Plus' },
        { value: 'gpt-4', label: 'GPT-4' },
      ]);
    }
  };

  // 初始化加载下拉数据
  useEffect(() => {
    fetchDomainOptions();
    fetchModelOptions();
  }, []);

  // 获取断言配置列表
  const fetchConfigs = async (pageNum = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        pageNum,
        pageSize,
        ...(filterRuleName && { ruleName: filterRuleName }),
        ...(filterDomainId && { domainId: filterDomainId }),
        ...(filterScenarioId && { scenarioId: filterScenarioId }),
        ...(filterType && { assertionType: filterType?.toUpperCase() }),
        ...(filterOwner && { onlyMine: filterOwner === 'mine' }),
      };
      
      const content = await shadowLinkApi.getAssertionConfigList(params);
      if (content) {
        const list = content.list?.map(transformBackendToFrontend) || [];
        setConfigs(list);
        setPagination({
          current: content.current || 1,
          pageSize: content.size || 10,
          total: content.total || 0,
          pages: content.pages || 1,
        });
      } else {
        // 接口返回失败，展示空数据
        setConfigs([]);
        setPagination({ current: 1, pageSize: 10, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('获取断言配置列表失败:', error);
      // 接口异常时展示空数据，不使用 mock
      setConfigs([]);
      setPagination({ current: 1, pageSize: 10, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [filterDomainId, filterScenarioId, filterType, filterOwner]);

  const makeEmptyConfig = () => ({
    id: Date.now(),
    name: '',
    domain: undefined,
    domainId: undefined,
    scenario: undefined,
    scenarioId: undefined,
    assertionType: 'engineering',
    enabled: true,
    engineering: { 
      code: `// 工程断言函数
// 参数：
//   aiOutput    - AI 输出的完整 JSON 对象
//   humanOutput - 人工决策的完整 JSON 对象
// 返回值：
//   { pass: boolean, reason: string }

function assert(aiOutput, humanOutput) {
  const aiDecision = aiOutput.decision;
  const humanDecision = humanOutput.human_decision;

  if (aiDecision === humanDecision) {
    return { pass: true, reason: \`决策一致：\${aiDecision}\` };
  }

  return {
    pass: false,
    reason: \`AI 决策 "\${aiDecision}" 与人工决策 "\${humanDecision}" 不一致\`
  };
}`
    },
    llm: { model: 'qwen3-max' },
    createdAt: new Date().toISOString().slice(0, 10),
    createdBy: currentUserWorkid,
    createdByName: currentUserName,
  });

  const openNewConfig = () => {
    setEditingConfig(makeEmptyConfig());
    setModalOpen(true);
  };

  const openEditConfig = (config) => {
    // 确保工程断言有默认代码
    const hasEngCode = config.engineering?.code && config.engineering.code.trim() !== '';
    const editingData = {
      ...config,
      engineering: {
        ...config.engineering,
        code: hasEngCode ? config.engineering.code : makeEmptyConfig().engineering.code
      },
      llm: { ...config.llm }
    };
    setEditingConfig(editingData);
    setModalOpen(true);
  };

  const getFieldPresets = (scenario) => {
    return SCENARIO_FIELD_PRESETS[scenario] || DEFAULT_FIELDS;
  };

  const handleScenarioChange = (scenarioId) => {
    // 根据scenarioId找到对应的scenarioName
    const scenarioOption = scenarioOptions.find(s => s.value === scenarioId);
    const scenarioName = scenarioOption ? scenarioOption.label : scenarioId;
    setEditingConfig(prev => ({ 
      ...prev, 
      scenarioId, 
      scenario: scenarioName,
      engineering: { ...prev.engineering }, 
      llm: { model: 'qwen-max' } 
    }));
  };

  const handleDomainChange = (domainId) => {
    // 根据domainId找到对应的domainName
    const domainOption = domainOptions.find(d => d.value === domainId);
    const domainName = domainOption ? domainOption.label : domainId;
    setEditingConfig(prev => ({ 
      ...prev, 
      domainId, 
      domain: domainName,
      scenarioId: undefined, 
      scenario: undefined,
      engineering: { ...prev.engineering }, 
      llm: { model: 'qwen-max' } 
    }));
    // 加载对应业务域的场景列表
    if (domainId) {
      fetchScenarioOptions(domainId);
    } else {
      setScenarioOptions([]);
    }
  };

  const handleAssertionTypeChange = (type) => {
    setEditingConfig(prev => ({
      ...prev,
      assertionType: type,
    }));
  };

  const findConflictingConfig = (targetConfig, excludeId) => {
    return configs.find(c =>
      c.id !== excludeId &&
      c.domain === targetConfig.domain &&
      c.scenario === targetConfig.scenario &&
      c.assertionType === targetConfig.assertionType
    );
  };

  const handleSave = async () => {
    if (!editingConfig.name || !editingConfig.domain || !editingConfig.scenario) return;
    
    // 工程断言时，确保有代码
    let configToSave = editingConfig;
    if (editingConfig.assertionType === 'engineering') {
      const hasCode = editingConfig.engineering?.code && editingConfig.engineering.code.trim() !== '';
      if (!hasCode) {
        configToSave = {
          ...editingConfig,
          engineering: { code: makeEmptyConfig().engineering.code }
        };
      }
    }
    
    try {
      const backendData = transformFrontendToBackend(configToSave);
      const isUpdate = configs.find(c => c.id === editingConfig.id);
      
      if (isUpdate) {
        // 更新操作 - 接口要求id放在body中
        await shadowLinkApi.updateAssertionConfig({ id: editingConfig.id, ...backendData });
        message.success('更新成功');
      } else {
        // 新建操作
        await shadowLinkApi.createAssertionConfig(backendData);
        message.success('创建成功');
      }
      
      setModalOpen(false);
      setEditingConfig(null);
      fetchConfigs(pagination.current, pagination.pageSize);
      fetchStats(); // 刷新统计数据
    } catch (error) {
      // 冲突检查由后端处理，这里只处理其他错误
      console.error('保存失败:', error);
      message.error(error.message || '保存失败');
      
      // 接口异常时本地保存（mock模式）
      const conflict = findConflictingConfig(editingConfig, editingConfig.id);
      if (conflict) {
        message.error(`保存失败：「${conflict.name}」已占用该场景的 ${editingConfig.assertionType === 'engineering' ? '工程断言' : 'LLM 断言'} 位置，每个场景每种断言类型只能有一条规则`);
        return;
      }
      setConfigs(prev => {
        const exists = prev.find(c => c.id === editingConfig.id);
        if (exists) {
          return prev.map(c => c.id === editingConfig.id ? editingConfig : c);
        }
        return [...prev, editingConfig];
      });
      setModalOpen(false);
      setEditingConfig(null);
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      // 接口要求enabled为字符串 "Y"/"N"
      await shadowLinkApi.toggleAssertionConfig(id, enabled ? 'Y' : 'N');
      message.success(enabled ? '已启用' : '已禁用');
      fetchConfigs(pagination.current, pagination.pageSize);
      fetchStats(); // 刷新统计数据
    } catch (error) {
      console.error('切换状态失败:', error);
      // 接口异常时本地更新（mock模式）
      setConfigs(prev => prev.map(c => c.id === id ? { ...c, enabled } : c));
    }
  };

  const handleDuplicate = async (config) => {
    try {
      // 调用后端复制接口，后端自动处理名称追加「 - 副本」和默认禁用
      await shadowLinkApi.copyAssertionConfig(config.id);
      message.success('复制成功');
      fetchConfigs(pagination.current, pagination.pageSize);
      fetchStats(); // 刷新统计数据
    } catch (error) {
      console.error('复制失败:', error);
      // 接口异常时本地复制（mock模式）
      // 确保工程断言有默认代码
      const hasEngCode = config.engineering?.code && config.engineering.code.trim() !== '';
      const duplicated = {
        ...config,
        id: Date.now(),
        name: `${config.name} - 副本`,
        enabled: false, // 副本默认禁用
        createdAt: new Date().toISOString().slice(0, 10),
        createdBy: currentUserWorkid,
        createdByName: currentUserName,
        engineering: config.assertionType === 'engineering' && !hasEngCode
          ? { code: makeEmptyConfig().engineering.code }
          : { ...config.engineering },
      };
      setConfigs(prev => [...prev, duplicated]);
    }
  };

  const handleDelete = async (id) => {
    try {
      await shadowLinkApi.deleteAssertionConfig(id);
      message.success('删除成功');
      fetchConfigs(pagination.current, pagination.pageSize);
      fetchStats(); // 刷新统计数据
    } catch (error) {
      console.error('删除失败:', error);
      // 接口异常时本地删除（mock模式）
      setConfigs(prev => prev.filter(c => c.id !== id));
    }
  };

  // 获取统计数据
  const [stats, setStats] = useState({
    engineeringCount: 0,
    llmCount: 0,
    enabledCount: 0,
    myCount: 0,
  });

  const fetchStats = async () => {
    try {
      const content = await shadowLinkApi.getAssertionConfigStats();
      if (content) {
        setStats({
          engineeringCount: content.engineeringCount || 0,
          llmCount: content.llmCount || 0,
          enabledCount: content.enabledCount || 0,
          myCount: content.myCount || 0,
        });
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      // 接口异常时展示空数据
      setStats({
        engineeringCount: 0,
        llmCount: 0,
        enabledCount: 0,
        myCount: 0,
      });
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handlePageChange = (page, pageSize) => {
    fetchConfigs(page, pageSize);
  };

  // 使用接口返回的统计数据
  const { engineeringCount, llmCount, enabledCount, myCount } = stats;

  // 筛选栏可用的场景选项（根据筛选的业务域动态加载）
  const [filterScenarioOptions, setFilterScenarioOptions] = useState([]);
  
  // 当筛选业务域变化时，加载对应的场景列表
  useEffect(() => {
    if (filterDomainId) {
      shadowLinkApi.getBusinessScenarios(filterDomainId)
        .then(res => setFilterScenarioOptions(res?.list || []))
        .catch(() => setFilterScenarioOptions([]));
    } else {
      setFilterScenarioOptions([]);
    }
  }, [filterDomainId]);

  const conflictWarning = useMemo(() => {
    if (!editingConfig?.domain || !editingConfig?.scenario || !editingConfig?.assertionType) return null;
    return findConflictingConfig(editingConfig, editingConfig.id);
  }, [editingConfig?.domain, editingConfig?.scenario, editingConfig?.assertionType, editingConfig?.id, configs]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <ThunderboltOutlined className="text-blue-500" />
              断言配置
              <Tooltip title="为每个业务场景配置断言规则，支持工程断言（代码规则）和 LLM 断言（语义判断）两种模式，二选一。规则由创建者独立维护，其他人只能复制。">
                <InfoCircleOutlined className="text-gray-400 text-base cursor-help" />
              </Tooltip>
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">配置 AI 与人工决策的对比断言规则，驱动影子链路一致性评测</p>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={openNewConfig}
            style={{ backgroundColor: '#1677ff', borderColor: '#1677ff' }}
          >
            新建断言规则
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card bordered={false} className="shadow-sm text-center">
          <div className="text-2xl font-bold text-blue-600">{engineeringCount}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <CodeOutlined className="text-blue-400" />工程断言规则
          </div>
        </Card>
        <Card bordered={false} className="shadow-sm text-center">
          <div className="text-2xl font-bold text-purple-600">{llmCount}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <BulbOutlined className="text-purple-400" />LLM 断言规则
          </div>
        </Card>
        <Card bordered={false} className="shadow-sm text-center">
          <div className="text-2xl font-bold text-green-600">{enabledCount}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <CheckCircleOutlined className="text-green-400" />已启用规则
          </div>
        </Card>
        <Card bordered={false} className="shadow-sm text-center">
          <div className="text-2xl font-bold text-orange-500">{myCount}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
            <UserOutlined className="text-orange-400" />我维护的规则
          </div>
        </Card>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            placeholder="搜索规则名称"
            allowClear
            style={{ width: 200 }}
            value={filterRuleName}
            onChange={(e) => setFilterRuleName(e.target.value)}
            onPressEnter={() => fetchConfigs(1, pagination.pageSize)}
          />
          <Select
            placeholder="筛选业务域"
            allowClear
            style={{ width: 130 }}
            value={filterDomainId}
            onChange={(val) => { setFilterDomainId(val); setFilterScenarioId(undefined); }}
          >
            {domainOptions.map(d => <Option key={d.value} value={d.value}>{d.label}</Option>)}
          </Select>
          <Select
            placeholder="筛选业务场景"
            allowClear
            style={{ width: 200 }}
            value={filterScenarioId}
            onChange={setFilterScenarioId}
            disabled={!filterDomainId}
          >
            {filterScenarioOptions.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
          </Select>
          <Select
            placeholder="筛选断言类型"
            allowClear
            style={{ width: 150 }}
            value={filterType}
            onChange={setFilterType}
          >
            <Option value="engineering"><CodeOutlined className="mr-1 text-blue-500" />工程断言</Option>
            <Option value="llm"><BulbOutlined className="mr-1 text-purple-500" />LLM 断言</Option>
          </Select>
          <Select
            placeholder="筛选维护者"
            allowClear
            style={{ width: 130 }}
            value={filterOwner}
            onChange={setFilterOwner}
          >
            <Option value="mine"><UserOutlined className="mr-1 text-blue-500" />我的规则</Option>
            <Option value="others"><LockOutlined className="mr-1 text-gray-400" />他人规则</Option>
          </Select>
          <Button onClick={() => fetchConfigs(1, pagination.pageSize)}>查询</Button>
          <span className="text-xs text-gray-400 ml-auto">共 {pagination.total} 条规则</span>
        </div>
      </div>

      <div>
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm py-16 text-center">
            <div className="text-gray-400">加载中...</div>
          </div>
        ) : configs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span className="text-gray-400">暂无断言规则，点击「新建断言规则」开始配置</span>}
              className="py-16"
            />
          </div>
        ) : (
          configs.map(config => (
            <ConfigCard
              key={config.id}
              config={config}
              onEdit={openEditConfig}
              onToggle={handleToggle}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              currentUserWorkid={currentUserWorkid}
              isAdmin={IS_ADMIN}
            />
          ))
        )}
      </div>

      {pagination.total > pagination.pageSize && (
        <div className="flex justify-end mt-4">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
          />
        </div>
      )}

      <Modal
        title={
          <div className="flex items-center gap-2">
            <SettingOutlined className="text-blue-500" />
            <span>{editingConfig?.id && configs.find(c => c.id === editingConfig.id) ? '编辑断言规则' : '新建断言规则'}</span>
          </div>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingConfig(null); }}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={680}
        okButtonProps={{ disabled: !editingConfig?.name || !editingConfig?.domain || !editingConfig?.scenario || !!conflictWarning }}
        destroyOnClose
      >
        {editingConfig && (
          <div className="space-y-5 pt-2">
            {conflictWarning && (
              <Alert
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message={
                  <span className="text-sm font-medium">唯一性冲突</span>
                }
                description={
                  <span className="text-xs text-gray-600">
                    该业务场景下已存在一条 <strong>{conflictWarning.assertionType === 'engineering' ? '工程断言' : 'LLM 断言'}</strong> 规则：
                    「{conflictWarning.name}」（由 {conflictWarning.createdByName} 创建）。
                    每个业务场景每种断言类型只能配置一条规则，请修改断言类型或选择其他场景。
                  </span>
                }
              />
            )}

            <div>
              <div className="text-xs text-gray-500 mb-1.5">规则名称 <span className="text-red-400">*</span></div>
              <input
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                placeholder="例：采购RT审核 - 决策字段精确匹配"
                value={editingConfig.name}
                onChange={(e) => setEditingConfig(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1.5">业务域 <span className="text-red-400">*</span></div>
                <Select
                  placeholder="选择业务域"
                  value={editingConfig.domain}
                  onChange={handleDomainChange}
                  style={{ width: '100%' }}
                >
                  {domainOptions.map(d => <Option key={d.value} value={d.value}>{d.label}</Option>)}
                </Select>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1.5">业务场景 <span className="text-red-400">*</span></div>
                <Select
                  placeholder="选择业务场景"
                  value={editingConfig.scenario}
                  onChange={handleScenarioChange}
                  style={{ width: '100%' }}
                  disabled={!editingConfig.domain}
                >
                  {scenarioOptions.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                </Select>
              </div>
            </div>

            <Divider className="my-4" />

            <div>
              <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                断言类型
                <Tooltip title="工程断言：用代码规则精确比对字段值，确定性强；LLM断言：用大模型语义理解判断字段是否一致，适合自由文本">
                  <InfoCircleOutlined className="text-gray-400 cursor-help" />
                </Tooltip>
                <span className="text-red-400">*</span>
              </div>
              <Radio.Group
                value={editingConfig.assertionType}
                onChange={(e) => handleAssertionTypeChange(e.target.value)}
                className="w-full"
              >
                <div className="grid grid-cols-2 gap-3">
                  <Radio.Button
                    value="engineering"
                    style={{ padding: '12px 16px', borderRadius: '8px', height: 'auto', display: 'flex', alignItems: 'flex-start' }}
                  >
                    <div>
                      <div className="flex items-center gap-1.5 font-semibold text-sm mb-1">
                        <CodeOutlined className="text-blue-500" />
                        工程断言
                      </div>
                      <div className="text-xs text-gray-400 font-normal leading-relaxed">
                        精确匹配 / 枚举映射 / 正则 / 数值范围，零幻觉、可解释
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button
                    value="llm"
                    style={{ padding: '12px 16px', borderRadius: '8px', height: 'auto', display: 'flex', alignItems: 'flex-start' }}
                  >
                    <div>
                      <div className="flex items-center gap-1.5 font-semibold text-sm mb-1">
                        <BulbOutlined className="text-purple-500" />
                        LLM 断言
                      </div>
                      <div className="text-xs text-gray-400 font-normal leading-relaxed">
                        语义理解判断，适合自由文本、复杂表述场景
                      </div>
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </div>

            <Divider className="my-4" />

            {editingConfig.assertionType === 'engineering' ? (
              <EngineeringAssertionForm
                config={editingConfig}
                onChange={setEditingConfig}
              />
            ) : (
              <LLMAssertionForm
                config={editingConfig}
                onChange={setEditingConfig}
                modelOptions={modelOptions}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AssertionConfig;
